import { N, round, ceil } from './helpers.mjs';

export const GROUNDWORKERS = [
  // ====================== GROUNDWORKERS / LANDSCAPERS ======================
  {
    slug: 'concrete-volume',
    trade: 'groundworkers',
    name: 'Concrete Volume',
    blurb: 'Foundations / slabs. Add 5–10% wastage.',
    inputs: [
      { key: 'length', label: 'Length', unit: 'm', placeholder: '5' },
      { key: 'width', label: 'Width', unit: 'm', placeholder: '3' },
      { key: 'depth', label: 'Depth', unit: 'm', placeholder: '0.15' },
      { key: 'wastage', label: 'Wastage', unit: '%', placeholder: '7', default: 7 },
    ],
    compute: (v) => {
      const vol = N(v.length) * N(v.width) * N(v.depth) * (1 + N(v.wastage) / 100);
      return [
        { label: 'Concrete needed', value: round(vol, 2), unit: 'm³' },
        { label: 'Ready-mix bags (40kg)', value: ceil(vol / 0.018), unit: 'bags' },
      ];
    },
  },
  {
    slug: 'hardcore-mot',
    trade: 'groundworkers',
    name: 'Aggregate Calculator',
    blurb: 'Work out tonnage, volume and bulk bags for sand, gravel, MOT Type 1/2/3, ballast, grano dust and other aggregates.',
    inputs: [
      { key: 'length', label: 'Length', unit: 'm', placeholder: '10' },
      { key: 'width', label: 'Width', unit: 'm', placeholder: '4' },
      { key: 'depth', label: 'Depth', unit: 'mm', placeholder: '150' },
      {
        key: 'material',
        label: 'Aggregate type',
        type: 'select',
        default: 'mot-type-1',
        options: [
          { value: 'sharp-sand',         label: 'Sharp sand (1.60 t/m³)' },
          { value: 'soft-sand',          label: 'Soft / building sand (1.50 t/m³)' },
          { value: 'ballast',            label: 'Ballast – sand + gravel (1.75 t/m³)' },
          { value: 'gravel-20mm',        label: '20mm gravel / shingle (1.50 t/m³)' },
          { value: 'gravel-14mm',        label: '14mm gravel (1.50 t/m³)' },
          { value: 'gravel-10mm',        label: '10mm gravel (1.50 t/m³)' },
          { value: 'gravel-6mm',         label: '6mm gravel / pea shingle (1.50 t/m³)' },
          { value: 'gravel-2-6mm',       label: '2–6mm gravel / chippings (1.50 t/m³)' },
          { value: 'decorative-slate',   label: 'Decorative slate chippings (1.30 t/m³)' },
          { value: 'mot-type-1',         label: 'MOT Type 1 – compacted (2.00 t/m³)' },
          { value: 'mot-type-2',         label: 'MOT Type 2 (1.90 t/m³)' },
          { value: 'mot-type-3',         label: 'MOT Type 3 (1.90 t/m³)' },
          { value: 'crushed-concrete',   label: 'Crushed concrete – Type 1 recycled (1.90 t/m³)' },
          { value: 'scalpings-6f2',      label: 'Sub-base / scalpings 6F2 (1.85 t/m³)' },
          { value: 'scalpings-6f5',      label: 'Sub-base / scalpings 6F5 (1.85 t/m³)' },
          { value: 'grano-dust',         label: 'Grano dust / granite dust (1.60 t/m³)' },
          { value: 'limestone-dust',     label: 'Limestone dust (1.50 t/m³)' },
          { value: 'sandstone-dust',     label: 'Sandstone dust (1.50 t/m³)' },
          { value: 'topsoil',            label: 'Topsoil / earth (1.50 t/m³)' },
          { value: 'cement',             label: 'Cement (1.44 t/m³)' },
        ],
      },
      { key: 'wastage', label: 'Wastage', unit: '%', placeholder: '5', default: 5 },
      { key: 'bagSize', label: 'Bulk bag size', unit: 't', placeholder: '0.85', default: 0.85 },
      { key: 'pricePerTonne', label: 'Price per tonne (optional)', unit: '£', placeholder: '0', default: 0 },
    ],
    compute: (v) => {
      const N = (x) => { const n = parseFloat(x); return Number.isFinite(n) ? n : 0; };
      const ceil = Math.ceil;
      const round = (x, d = 2) => Math.round(x * Math.pow(10, d)) / Math.pow(10, d);
      const DENSITY = {
        'sharp-sand': 1.60, 'soft-sand': 1.50, 'ballast': 1.75,
        'gravel-20mm': 1.50, 'gravel-14mm': 1.50, 'gravel-10mm': 1.50,
        'gravel-6mm': 1.50, 'gravel-2-6mm': 1.50, 'decorative-slate': 1.30,
        'mot-type-1': 2.00, 'mot-type-2': 1.90, 'mot-type-3': 1.90,
        'crushed-concrete': 1.90, 'scalpings-6f2': 1.85, 'scalpings-6f5': 1.85,
        'grano-dust': 1.60, 'limestone-dust': 1.50, 'sandstone-dust': 1.50,
        'topsoil': 1.50, 'cement': 1.44,
      };
      const material = v.material || 'mot-type-1';
      const density = DENSITY[material] || 2.00;
      const wastage = N(v.wastage) || 0;
      const bagSize = Math.max(0.05, N(v.bagSize) || 0.85);
      const pricePerTonne = N(v.pricePerTonne) || 0;
      const area = N(v.length) * N(v.width);
      const volRaw = area * (N(v.depth) / 1000);
      const vol = volRaw * (1 + wastage / 100);
      const tonnage = vol * density;
      const out = [
        { label: 'Area', value: round(area, 2), unit: 'm²' },
        { label: `Volume (inc. ${wastage}% wastage)`, value: round(vol, 2), unit: 'm³' },
        { label: 'Tonnage', value: round(tonnage, 2), unit: 'tonnes' },
        { label: `Bulk bags (${bagSize} t each)`, value: ceil(tonnage / bagSize), unit: 'bags' },
      ];
      if (pricePerTonne > 0) {
        out.push({ label: 'Estimated cost', value: round(tonnage * pricePerTonne, 2), unit: '£' });
      }
      return out;
    },
  },
  {
    slug: 'topsoil',
    trade: 'groundworkers',
    name: 'Topsoil / Turf Soil & Turf Rolls',
    blurb: 'Topsoil tonnage + bulk bags, plus how many turf rolls you need for the same area. UK standard turf roll = 1 m × 0.6 m (0.6 m² per roll).',
    inputs: [
      { key: 'length', label: 'Length', unit: 'm', placeholder: '8' },
      { key: 'width', label: 'Width', unit: 'm', placeholder: '4' },
      { key: 'depth', label: 'Soil depth', unit: 'mm', placeholder: '100' },
      { key: 'rollArea', label: 'Turf roll area', unit: 'm²/roll', placeholder: '0.6', default: 0.6 },
      { key: 'turfWastage', label: 'Turf wastage', unit: '%', placeholder: '5', default: 5 },
    ],
    compute: (v) => {
      const length = N(v.length);
      const width = N(v.width);
      const area = length * width;
      const vol = area * (N(v.depth) / 1000);
      const rollArea = Math.max(0.05, N(v.rollArea) || 0.6);
      const turfWaste = N(v.turfWastage) || 0;
      const rolls = ceil((area * (1 + turfWaste / 100)) / rollArea);
      return [
        { label: 'Area', value: round(area, 2), unit: 'm²' },
        { label: 'Soil volume', value: round(vol, 2), unit: 'm³' },
        { label: 'Soil tonnage', value: round(vol * 1.6, 2), unit: 'tonnes' },
        { label: 'Bulk bags (0.85m³)', value: ceil(vol / 0.85), unit: 'bags' },
        { label: `Turf rolls (inc. ${turfWaste}% wastage)`, value: rolls, unit: 'rolls' },
      ];
    },
  },
  {
    slug: 'resin-driveway',
    trade: 'groundworkers',
    name: 'Resin Driveway Calculator',
    blurb: 'Resin Bound & Resin Bonded surfacing — based on Vuba & Daltex systems. Works out kits, resin, aggregate, kiln-dried sand and primer base coat. Standard 7.5kg UVR kit ≈ 3.55 m² @ 18mm (driveway) / 4.0 m² @ 15mm (foot traffic).',
    inputs: [
      { key: 'length', label: 'Area length', unit: 'm', placeholder: '10' },
      { key: 'width', label: 'Area width', unit: 'm', placeholder: '4' },
      {
        key: 'system',
        label: 'System',
        type: 'select',
        default: 'resin-bound',
        options: [
          { value: 'resin-bound',  label: 'Resin Bound (resin + stone mixed, permeable)' },
          { value: 'resin-bonded', label: 'Resin Bonded (resin base coat, stone scattered on top)' },
        ],
      },
      {
        key: 'boundUse',
        label: 'Depth / use — Resin Bound only',
        type: 'select',
        default: 'driveway-18',
        options: [
          { value: 'driveway-18', label: 'Driveway / vehicle — 18mm (3.55 m²/kit)' },
          { value: 'foot-15',     label: 'Foot traffic / patio — 15mm (4.0 m²/kit)' },
        ],
      },
      { key: 'kitResinKg', label: 'Resin per kit', unit: 'kg', placeholder: '7.5', default: 7.5 },
      { key: 'bondedResinRate', label: 'Bonded resin spread rate — Resin Bonded only', unit: 'kg/m²', placeholder: '1.5', default: 1.5 },
      { key: 'bondedAggRate', label: 'Bonded aggregate scatter — Resin Bonded only', unit: 'kg/m²', placeholder: '6', default: 6 },
      {
        key: 'primer',
        label: 'Include primer base coat?',
        type: 'select',
        default: 'yes',
        options: [
          { value: 'yes', label: 'Yes — prime the prepared base first' },
          { value: 'no',  label: 'No' },
        ],
      },
      { key: 'primerCoverage', label: 'Primer coverage', unit: 'm²/kg', placeholder: '4', default: 4 },
      { key: 'wastage', label: 'Wastage', unit: '%', placeholder: '10', default: 10 },
      { key: 'pricePerKit', label: 'Price per resin kit (optional)', unit: '£', placeholder: '0', default: 0 },
    ],
    compute: (v) => {
      const N = (x) => { const n = parseFloat(x); return Number.isFinite(n) ? n : 0; };
      const ceil = Math.ceil;
      const round = (x, d = 2) => Math.round(x * Math.pow(10, d)) / Math.pow(10, d);

      const area = N(v.length) * N(v.width);
      const wastage = N(v.wastage) || 0;
      const areaW = area * (1 + wastage / 100);
      const kitResin = Math.max(0.1, N(v.kitResinKg) || 7.5);
      const pricePerKit = N(v.pricePerKit) || 0;
      const system = v.system || 'resin-bound';

      const out = [
        { label: 'Surface area', value: round(area, 2), unit: 'm²' },
        { label: `Area inc. ${wastage}% wastage`, value: round(areaW, 2), unit: 'm²' },
      ];

      if (system === 'resin-bound') {
        // Daltex UVR reference: 7.5kg kit = 3.55 m² @18mm, 4.0 m² @15mm.
        // Mix per 7.5kg kit: 75kg (2–5mm) + 25kg (1–3mm) + 6.25kg C52 kiln-dried sand.
        const baseCoverage = v.boundUse === 'foot-15' ? 4.0 : 3.55;
        const coveragePerKit = baseCoverage * (kitResin / 7.5);
        const kits = ceil(areaW / coveragePerKit);
        const ratio = kitResin / 7.5;
        const resinKg = round(kits * kitResin, 2);
        const agg25 = kits * 75 * ratio;   // 2–5mm
        const agg13 = kits * 25 * ratio;   // 1–3mm
        const aggTotal = agg25 + agg13;
        const sandKg = round(kits * 6.25 * ratio, 2);
        out.push(
          { label: `Coverage per kit (${v.boundUse === 'foot-15' ? '15mm' : '18mm'})`, value: round(coveragePerKit, 2), unit: 'm²/kit' },
          { label: `Resin kits to order (${kitResin}kg)`, value: kits, unit: 'kits' },
          { label: 'Total resin', value: resinKg, unit: 'kg' },
          { label: 'Aggregate 2–5mm', value: round(agg25, 0), unit: 'kg' },
          { label: 'Aggregate 1–3mm', value: round(agg13, 0), unit: 'kg' },
          { label: 'Total aggregate', value: round(aggTotal, 0), unit: 'kg' },
          { label: 'Aggregate 25kg bags', value: ceil(aggTotal / 25), unit: 'bags' },
          { label: 'Kiln-dried sand (C52)', value: sandKg, unit: 'kg' },
        );
        if (pricePerKit > 0) out.push({ label: 'Resin cost', value: round(kits * pricePerKit, 2), unit: '£' });
      } else {
        // Resin Bonded (scatter coat): thin resin base + broadcast aggregate.
        const resinRate = Math.max(0.1, N(v.bondedResinRate) || 1.5);
        const aggRate = Math.max(0.1, N(v.bondedAggRate) || 6);
        const resinKg = areaW * resinRate;
        const kits = ceil(resinKg / kitResin);
        const aggKg = areaW * aggRate;
        out.push(
          { label: `Resin (base coat @ ${resinRate} kg/m²)`, value: round(resinKg, 2), unit: 'kg' },
          { label: `Resin kits to order (${kitResin}kg)`, value: kits, unit: 'kits' },
          { label: `Aggregate scatter (@ ${aggRate} kg/m²)`, value: round(aggKg, 0), unit: 'kg' },
          { label: 'Aggregate 25kg bags', value: ceil(aggKg / 25), unit: 'bags' },
        );
        if (pricePerKit > 0) out.push({ label: 'Resin cost', value: round(kits * pricePerKit, 2), unit: '£' });
      }

      if (v.primer === 'yes') {
        const primerCov = Math.max(0.1, N(v.primerCoverage) || 4);
        const primerKg = ceil(area / primerCov);
        out.push({ label: `Primer base coat (@ ${primerCov} m²/kg)`, value: primerKg, unit: 'kg' });
      }

      return out;
    },
  },
  {
    slug: 'sleeper-retaining-wall',
    trade: 'groundworkers',
    name: 'Sleeper Retaining Wall',
    blurb: 'Sleepers, support posts and Postcrete for a sleeper retaining wall. Standard sleeper 2.4m × 200 × 100mm. Posts at 1.2–1.5m centres; 2 × 20kg Postcrete bags per post.',
    inputs: [
      { key: 'length', label: 'Wall length', unit: 'm', placeholder: '6' },
      { key: 'height', label: 'Wall height', unit: 'm', placeholder: '0.6' },
      { key: 'sleeperLen', label: 'Sleeper length', unit: 'm', placeholder: '2.4', default: 2.4 },
      { key: 'orientation', label: 'Sleeper laid', type: 'select', default: 'flat',
        options: [
          { value: 'flat', label: 'Flat (200mm face up → 100mm course height)' },
          { value: 'upright', label: 'On edge (100mm face up → 200mm course height)' },
        ] },
      { key: 'postSpacing', label: 'Support post spacing', unit: 'm', placeholder: '1.5', default: 1.5 },
      { key: 'wastage', label: 'Wastage', unit: '%', placeholder: '5', default: 5 },
    ],
    compute: (v) => {
      const length = N(v.length);
      const height = N(v.height);
      const sleeperLen = N(v.sleeperLen) || 2.4;
      const courseH = v.orientation === 'upright' ? 0.20 : 0.10;
      const courses = height > 0 ? ceil(height / courseH) : 0;
      const perCourse = length > 0 ? ceil(length / sleeperLen) : 0;
      const base = courses * perCourse;
      const sleepers = ceil(base * (1 + N(v.wastage) / 100));
      const postSpacing = Math.max(0.5, N(v.postSpacing) || 1.5);
      const posts = length > 0 ? ceil(length / postSpacing) + 1 : 0;
      const postcrete = posts * 2;
      return [
        { label: 'Courses', value: courses, unit: '' },
        { label: 'Sleepers per course', value: perCourse, unit: '' },
        { label: 'Sleepers (incl. wastage)', value: sleepers, unit: 'sleepers' },
        { label: `Support posts (@${postSpacing}m)`, value: posts, unit: 'posts' },
        { label: 'Postcrete (20kg bags)', value: postcrete, unit: 'bags' },
      ];
    },
  },
  {
    slug: 'tarmac-driveway',
    trade: 'groundworkers',
    name: 'Tarmac / Asphalt Calculator',
    blurb: 'Tonnage of tarmac for a drive or path across the common UK mixes — Asphalt Concrete (AC), Stone Mastic Asphalt (SMA), Hot Rolled Asphalt (HRA) and Permeable — plus MOT Type 1 sub-base, bitumen tack coat and cold-lay 25kg bags for small repairs. Density is set automatically from the mix you pick.',
    inputs: [
      { key: 'length', label: 'Length', unit: 'm', placeholder: '10' },
      { key: 'width', label: 'Width', unit: 'm', placeholder: '4' },
      { key: 'depth', label: 'Tarmac depth (compacted)', unit: 'mm', placeholder: '50', default: 50 },
      {
        key: 'mixType',
        label: 'Asphalt mix / surface type',
        type: 'select',
        default: 'ac',
        options: [
          { value: 'ac',   label: 'Asphalt Concrete (AC) — common, cost-effective (2.30 t/m³)' },
          { value: 'sma',  label: 'Stone Mastic Asphalt (SMA) — deformation & scuff resistant (2.35 t/m³)' },
          { value: 'hra',  label: 'Hot Rolled Asphalt (HRA) — high texture & skid resistance (2.40 t/m³)' },
          { value: 'perm', label: 'Permeable / porous (Ultiporous) — SuDS-compliant, drains through (2.00 t/m³)' },
        ],
      },
      { key: 'subBase', label: 'Sub-base (MOT Type 1) depth', unit: 'mm', placeholder: '100', default: 100 },
      { key: 'wastage', label: 'Wastage', unit: '%', placeholder: '5', default: 5 },
      { key: 'pricePerTonne', label: 'Tarmac price per tonne (optional)', unit: '£', placeholder: '0', default: 0 },
    ],
    compute: (v) => {
      const DENS = { ac: 2.30, sma: 2.35, hra: 2.40, perm: 2.00 };
      const mix = v.mixType || 'ac';
      const density = DENS[mix] || 2.30;
      const area = N(v.length) * N(v.width);
      const waste = 1 + N(v.wastage) / 100;
      const tarVol = area * (N(v.depth) / 1000) * waste;
      const tarTonnes = tarVol * density;
      const subVol = area * (N(v.subBase) / 1000) * waste;
      const subTonnes = subVol * 2.0; // MOT Type 1 ≈ 2.0 t/m³
      const bags = ceil((tarTonnes * 1000) / 25); // cold-lay 25kg bag equivalent
      const tack = area * 0.5; // bitumen tack coat ≈ 0.5 kg/m²
      const out = [
        { label: 'Surface type', value: mix.toUpperCase(), unit: '' },
        { label: 'Density used', value: density, unit: 't/m³' },
        { label: 'Area', value: round(area, 2), unit: 'm²' },
        { label: 'Tarmac volume', value: round(tarVol, 2), unit: 'm³' },
        { label: 'Tarmac (hot-lay)', value: round(tarTonnes, 2), unit: 'tonnes' },
        { label: 'Cold-lay 25kg bags (small jobs)', value: bags, unit: 'bags' },
        { label: 'MOT Type 1 sub-base', value: round(subTonnes, 2), unit: 'tonnes' },
        { label: 'Bitumen tack coat', value: round(tack, 1), unit: 'kg' },
      ];
      if (N(v.pricePerTonne) > 0) out.push({ label: 'Tarmac cost', value: round(tarTonnes * N(v.pricePerTonne), 2), unit: '£' });
      return out;
    },
  },

];
